import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';
import { 
  Building2, 
  CheckCircle, 
  Loader2, 
  ArrowLeft,
  AlertCircle
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BankInfoPage() {
  const [bankData, setBankData] = useState({ account_number: '', routing_number: '', bank_name: '' });
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchExisting = async () => {
      try {
        const res = await axios.get(`${API_URL}/user/bank-info`, { headers: getAuthHeaders() });
        if (res.data) {
          setBankData({
            account_number: res.data.account_number || '',
            routing_number: res.data.routing_number || '',
            bank_name: res.data.bank_name || ''
          });
          setAgreed(res.data.agreed_to_terms || false);
          setSubmitted(true);
        }
      } catch { toast.error('Could not load your saved bank info. You can still submit it.'); }
      finally { setLoading(false); }
    };
    fetchExisting();
  }, [getAuthHeaders]);

  const handleSubmit = async () => {
    if (!bankData.account_number || !bankData.routing_number || !bankData.bank_name) {
      toast.error('Please fill in all bank information fields.');
      return;
    }
    if (!agreed) {
      toast.error('You must agree to the payment terms to continue.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/user/bank-info`, {
        ...bankData,
        agreed_to_terms: agreed
      }, { headers: getAuthHeaders() });
      toast.success('Bank information saved successfully!');
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save bank information.');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center">
        <Loader2 className="animate-spin text-violet-500" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0F]">
      <header className="bg-[#12121A] border-b border-slate-800">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size="sm" />
            <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
              Bank Information
            </h1>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-slate-400 hover:text-white"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft size={16} className="mr-2" />
            Dashboard
          </Button>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12">
        {/* Welcome Message */}
        <div className="mb-6 p-5 bg-[#12121A] rounded-xl border border-violet-500/20 text-center">
          <Building2 className="text-violet-400 mx-auto mb-3" size={32} />
          <h2 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
            Welcome to Mighty Link Bank Information Portal
          </h2>
          <p className="text-sm text-slate-400 leading-relaxed max-w-lg mx-auto">
            This page is for submitting your banking information so that Mighty Link can process your payments via direct deposit. Please fill out the form below with your accurate bank details.
          </p>
        </div>

        {submitted && (
          <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl flex items-center gap-3" data-testid="bank-info-submitted">
            <CheckCircle className="text-emerald-400 flex-shrink-0" size={22} />
            <p className="text-emerald-300 text-sm">Your bank information has been submitted successfully. You can update it below if needed.</p>
          </div>
        )}

        <div className="bg-[#12121A] rounded-2xl border border-slate-800 overflow-hidden">
          {/* Payment Terms Message */}
          <div className="p-6 sm:p-8 bg-gradient-to-b from-violet-500/5 to-transparent border-b border-slate-800">
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle className="text-violet-400 flex-shrink-0 mt-0.5" size={22} />
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                Direct Deposit Payment Information
              </h2>
            </div>
            <div className="text-sm text-slate-300 leading-relaxed space-y-3 ml-9">
              <p>
                All technician payments are processed via <strong className="text-white">direct deposit</strong> to the bank account you provide below.
              </p>
              <p>
                Your first payment will be processed within a <strong className="text-white">21-day period</strong> from your start date. After that initial period, payments will proceed on a <strong className="text-white">weekly basis</strong>.
              </p>
              <p>
                Please ensure all bank details are accurate. Incorrect information may result in payment delays.
              </p>
            </div>
          </div>

          {/* Bank Info Form */}
          <div className="p-6 sm:p-8 space-y-5">
            <div>
              <Label htmlFor="bank_name" className="text-slate-300 text-sm">Bank Name *</Label>
              <Input
                id="bank_name"
                value={bankData.bank_name}
                onChange={(e) => setBankData(prev => ({ ...prev, bank_name: e.target.value }))}
                placeholder="e.g. Chase, Bank of America, Wells Fargo"
                className="mt-1.5 bg-[#0A0A0F] border-slate-700 text-white h-12 focus:border-violet-500"
                data-testid="input-bank-name"
              />
            </div>

            <div>
              <Label htmlFor="routing_number" className="text-slate-300 text-sm">Routing Number *</Label>
              <Input
                id="routing_number"
                value={bankData.routing_number}
                onChange={(e) => setBankData(prev => ({ ...prev, routing_number: e.target.value }))}
                placeholder="9-digit routing number"
                className="mt-1.5 bg-[#0A0A0F] border-slate-700 text-white h-12 font-mono focus:border-violet-500"
                data-testid="input-routing-number"
              />
            </div>

            <div>
              <Label htmlFor="account_number" className="text-slate-300 text-sm">Account Number *</Label>
              <Input
                id="account_number"
                value={bankData.account_number}
                onChange={(e) => setBankData(prev => ({ ...prev, account_number: e.target.value }))}
                placeholder="Your bank account number"
                className="mt-1.5 bg-[#0A0A0F] border-slate-700 text-white h-12 font-mono focus:border-violet-500"
                data-testid="input-account-number"
              />
            </div>

            {/* Agreement Checkbox */}
            <div className="pt-4 border-t border-slate-800">
              <div className="flex items-start gap-3 p-4 bg-[#0A0A0F] rounded-lg border border-slate-700">
                <Checkbox
                  id="agree_terms"
                  checked={agreed}
                  onCheckedChange={(checked) => setAgreed(checked)}
                  className="mt-0.5"
                  data-testid="checkbox-agree-terms"
                />
                <Label htmlFor="agree_terms" className="text-sm text-slate-300 leading-relaxed cursor-pointer">
                  I understand and agree that my first payment will be processed within a 21-day period from my start date, and subsequent payments will be made on a weekly basis via direct deposit to the bank account provided above.
                </Label>
              </div>
            </div>

            <Button
              onClick={handleSubmit}
              disabled={submitting || !agreed || !bankData.account_number || !bankData.routing_number || !bankData.bank_name}
              className="w-full h-12 btn-gradient text-white font-semibold text-base"
              data-testid="submit-bank-info"
            >
              {submitting ? (
                <Loader2 className="animate-spin mr-2" size={18} />
              ) : (
                <Building2 size={18} className="mr-2" />
              )}
              {submitted ? 'Update Bank Information' : 'Submit Bank Information'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
